import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, FolderOpen, Layers, Plus } from "lucide-react";
import { listJobs } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function BatchList({ jobId, onOpen, onNewBatch }) {
  const [jobs, setJobs] = useState([]);
  const [expanded, setExpanded] = useState(!jobId);

  const refresh = useCallback(async () => {
    try {
      setJobs(await listJobs());
    } catch (e) {
      console.warn("listJobs failed:", e?.response?.status || e.message);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, jobId]);

  const visible = jobs.filter((j) => j.total_files > 0 || j.id === jobId);
  if (visible.length === 0) return null;

  return (
    <section data-testid="batch-list" className="border-2 border-black">
      <button
        data-testid="batch-list-toggle"
        onClick={() => { setExpanded((v) => !v); if (!expanded) refresh(); }}
        className="w-full flex items-center justify-between px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] bg-black text-white"
      >
        <span className="inline-flex items-center gap-2">
          <Layers className="w-4 h-4" /> Batches ({visible.length}) — shared with your team
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="divide-y divide-black/10">
          {visible.map((j) => {
            const current = j.id === jobId;
            return (
              <div
                key={j.id}
                data-testid={`batch-row-${j.id}`}
                className={"flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] " + (current ? "bg-zinc-100" : "bg-white")}
              >
                <span className="w-40 shrink-0">{fmtDate(j.created_at)}</span>
                <span className="flex-1 min-w-[140px] truncate normal-case tracking-normal opacity-80" title={j.label || ""}>
                  {j.label || "Direct PDF upload"}
                </span>
                <span>{j.total_files} files</span>
                <span className="font-bold">{j.qualifying} qualifying</span>
                {j.failed > 0 && <span className="text-red-600">{j.failed} failed</span>}
                {j.pending > 0 && <span className="opacity-60">{j.pending} pending</span>}
                {current ? (
                  <span data-testid="batch-current-tag" className="border border-black px-2 py-0.5 text-[10px] font-bold">Open</span>
                ) : (
                  <button
                    data-testid={`batch-open-${j.id}`}
                    onClick={() => onOpen(j.id)}
                    className="inline-flex items-center gap-1 border border-black bg-white px-2.5 py-1 text-[10px] hover:bg-black hover:text-white"
                  >
                    <FolderOpen className="w-3 h-3" /> View
                  </button>
                )}
              </div>
            );
          })}
          <div className="px-4 py-2 bg-zinc-50 flex justify-end">
            <button
              data-testid="new-batch-btn"
              onClick={onNewBatch}
              className="inline-flex items-center gap-1 border border-black bg-white px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-black hover:text-white"
              title="Start a fresh batch (existing batches stay in this list)"
            >
              <Plus className="w-3 h-3" /> New batch
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
